import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  Stack,
  Typography,
  InputAdornment,
} from "@mui/material";
import {
  MdPerson,
  MdBusiness,
  MdVerified,
  MdLocationOn,
  MdPhone,
  MdEmail,
  MdCalendarToday,
} from "react-icons/md";

import { T } from "theme/tokens";
import * as api from "./api";

/**
 * Edit the fields an admin is allowed to change on a pharmacy.
 *
 * The fields here mirror the backend's allow-list exactly — anything else is
 * rejected server-side, and offering an input for a field that silently will
 * not save is worse than not offering it. Bank details and location are
 * deliberately absent: both have their own audited screens in the console,
 * with validation this form does not have.
 *
 * Only changed fields are sent, so the audit log records what someone
 * actually edited rather than one row per field on the form.
 */
const FIELDS = [
  { key: "bussinessName", label: "Pharmacy name", icon: <MdBusiness size={18} /> },
  { key: "fullName", label: "Owner name", icon: <MdPerson size={18} /> },
  { key: "businessPhone", label: "Business phone", icon: <MdPhone size={18} /> },
  { key: "ownerPhone", label: "Owner phone", icon: <MdPhone size={18} /> },
  { key: "email", label: "Email", icon: <MdEmail size={18} />, full: true },
  { key: "address", label: "Address", icon: <MdLocationOn size={18} />, full: true },
  { key: "pincode", label: "Pincode", icon: <MdLocationOn size={18} /> },
  { key: "gstNo", label: "GST no", icon: <MdBusiness size={18} /> },
  { key: "bussinessRegNo", label: "Business reg no", icon: <MdBusiness size={18} /> },
  { key: "medicalLicenseNo", label: "Medical licence no", icon: <MdVerified size={18} /> },
  { key: "drugLicenseNo", label: "Drug licence no", icon: <MdVerified size={18} /> },
  {
    key: "dateOfRegistration",
    label: "Registration date",
    icon: <MdCalendarToday size={18} />,
  },
  {
    key: "dateOfMedicalLicense",
    label: "Licence date",
    icon: <MdCalendarToday size={18} />,
  },
];

const blank = () =>
  FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: "" }), {});

export default function EditPharmacyDialog({ open, pharmacy, onClose, onSaved }) {
  const [values, setValues] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !pharmacy) return;
    setError("");
    setSaving(false);
    setValues(
      FIELDS.reduce(
        (acc, field) => ({ ...acc, [field.key]: pharmacy[field.key] ?? "" }),
        {}
      )
    );
  }, [open, pharmacy]);

  const changed = FIELDS.filter(
    (field) => String(values[field.key] ?? "") !== String(pharmacy?.[field.key] ?? "")
  );

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const body = changed.reduce(
        (acc, field) => ({ ...acc, [field.key]: values[field.key] }),
        {}
      );
      await api.updateProfile(pharmacy.pharmacyId, body);
      onSaved?.(`${changed.length} field(s) updated.`);
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Could not save those changes."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: T.ink }}>
          Edit {pharmacy?.bussinessName || "pharmacy"}
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: T.muted, fontFamily: "monospace" }}>
          {pharmacy?.medstownid || pharmacy?.pharmacyId}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.82rem" }}>
            Bank details and map location are edited in the pharmacy console —
            both are validated and audited separately.
          </Alert>

          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            {FIELDS.map((field) => (
              <Grid item xs={12} sm={field.full ? 12 : 6} key={field.key}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  value={values[field.key] ?? ""}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [field.key]: e.target.value }))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">{field.icon}</InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>
            ))}
          </Grid>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Typography sx={{ fontSize: "0.78rem", color: T.subtle, mr: "auto" }}>
          {changed.length
            ? `${changed.length} change(s) pending`
            : "Nothing changed yet"}
        </Typography>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: "none", color: T.muted }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !changed.length}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            bgcolor: T.primary,
            "&:hover": { bgcolor: "#013838" },
          }}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
